import type { AiReviewLocation } from "@/features/takeoff-pilot/ai-review-groups";
import { AiReviewDock } from "@/features/takeoff-pilot/ai-review-dock";
import { isAiSetupPending } from "@/features/takeoff-pilot/ai-takeoff-setup";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { AI_COUNTS_ENABLED } from "@/features/takeoff-pilot/ai-count-overlay";
import { notifyError } from "@/lib/notify-error";
import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  type ReactNode,
  type RefObject,
} from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useAction, useConvex, useMutation } from "convex/react";
import { RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import {
  useJobStatus,
  useComputedValues,
  useResolvedViewerSources,
  useTakeoffSelectionRecount,
} from "@/hooks/use-takeoff-pilot";
import {
  useScheduleOverrides,
  useUpsertScheduleOverride,
  useAddScheduleOverrideColumn,
  useRemoveScheduleOverrideCells,
  useResetScheduleOverride,
} from "@/hooks/useScheduleOverrides";
import {
  SpinnerState,
  ErrorState,
  CopyingRevisionState,
  ExtractingState,
  DetailsErrorState,
  CountsErrorState,
} from "@/components/page-states";
import { RowDetailsDialog } from "@/components/row-details-dialog";
import {
  getBottomPanelMaxHeight,
  ResizableBottomPanel,
} from "@/features/takeoff-pilot/resizable-bottom-panel";
import { PreparingDocumentsState, resolveActiveViewerSource } from "@/features/document-viewport/preparing-documents";
import { PanelHeader } from "@/features/takeoff-pilot/panel-header";
import type { DocumentTagSearchResponse } from "@/features/takeoff-pilot/search-document-tags-dialog";
import { FloatingPanelToggle } from "@/features/takeoff-pilot/floating-panel-toggle";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type {
  CountRow,
  LinkedSchedulePreviewSummary,
  MeasurementBreakdownTargetRef,
  ScheduleOverride,
  TagRecord,
  ViewerSource,
} from "@/types/takeoff-pilot";
import { withExportExtension } from "@/lib/export-filenames";
import { ExportDialog, lastFileFromArtifact, type ExportFormat } from "@/features/exports/export-dialog";
import { ExportOptionCheckbox, ExportOptionGroup } from "@/features/exports/export-options";
import { exportTarget } from "@/features/exports/export-names";
import { useExportFilenames } from "@/features/exports/use-export-filenames";
import { DEFAULT_TAG_COLOR } from "@/lib/tag-colors";
import { useViewerExit } from "@/components/layout/use-viewer-exit";
import { ViewerBackButton, ViewerTopBar } from "@/components/layout/viewer-top-bar";
import { Button } from "@/components/ui/button";
import { MultiSelect, type MultiSelectSection } from "@/components/ui/multi-select";
import { RowAction, RowActionSeparator, RowActionsMenu } from "@/components/row-actions-menu";
import {
  RowExpanderButton,
  RowExpanderSpacer,
} from "@/features/document-viewport/row-expander-button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TakeoffResultsViewer,
  type TakeoffResultsViewerHandle,
} from "@/features/takeoff-pilot/takeoff-results-viewer";
import { useTakeoffJobHistory, type TakeoffJobHistory } from "@/features/takeoff-pilot/use-takeoff-job-history";
import { TagInstanceNavigationButton } from "@/features/takeoff-pilot/tag-instance-navigation-button";
import {
  UnitCostInput,
  ScheduleCellInput,
  CustomLineItemNameInput,
  TagColorInput,
} from "@/features/takeoff-pilot/tag-results-table-inputs";
import { useRouteParam } from "@/lib/use-route-param";
import { ActionLogProvider, useLoggedMutation } from "@/hooks/use-action-log-session";
import {
  GLOBAL_PROCESSING_COMPLETED,
  GLOBAL_PROCESSING_FAILED,
  GLOBAL_PROCESSING_PENDING,
  GLOBAL_PROCESSING_PROCESSING,
  toProcessingStateWithIdle,
  type ProcessingStateWithIdle,
} from "@/lib/processing-states";
import {
  compareTagsByRawValue,
  getTotalCost,
  formatCurrency,
  formatMeasurementTotal,
  groupMeasurementBreakdownByDisplayValue,
  downloadTakeoffCsv,
  keyLinkedSchedulePreviewCells,
} from "@/features/takeoff-pilot/takeoff-utils";
import { MEASUREMENT_TYPES } from "@/lib/measurement-domain";
import type { TakeoffCostBasis } from "@/lib/takeoff-domain";
import type { ScheduleColumnOption } from "@/types/takeoff-pilot";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

export const Route = createFileRoute("/takeoffPilot/$jobId")({
  component: TakeoffPilotJobPage,
});

const COST_COLUMNS_VISIBLE_STORAGE_KEY = "takeoff-cost-columns-visible";
const RESULT_QUANTITY_COLUMNS = ["count", "lf", "sqft", "cf"] as const;
const RESULT_TABLE_COLUMN_WIDTHS_REM = {
  actions: 2,
  color: 2.25,
  tag: 18,
  schedule: 11,
  quantity: 4,
  tagOccurrences: 7.5,
  measuredItems: 12,
  unitCost: 7.5,
  totalCost: 6,
} as const;

type ResultQuantityColumn = (typeof RESULT_QUANTITY_COLUMNS)[number];
const PDF_EXPORT_MEASUREMENT_COLUMNS = ["Count", "LF", "SQFT", "CF"] as const;
const PDF_EXPORT_SCHEDULE_COLUMN_PREFIX = "schedule:";

function encodePdfExportScheduleColumn(column: string) {
  return `${PDF_EXPORT_SCHEDULE_COLUMN_PREFIX}${column}`;
}

function mergeScheduleColumns(args: {
  linkedColumns: ScheduleColumnOption[];
  overrideHeaders: string[];
}) {
  const columns = new Map<string, ScheduleColumnOption>(
    args.linkedColumns.map((column) => [column.value, column] as const)
  );

  for (const header of args.overrideHeaders) {
    if (!columns.has(header)) {
      columns.set(header, {
        value: header,
        label: header,
        isCustom: true,
      });
    }
  }

  return Array.from(columns.values());
}

function TagMeasurementCounts({ result }: { result: CountRow | undefined }) {
  return (
    <>
      <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] tabular-nums">
        {result ? (result.tagOccurrenceCount ?? result.count) : "—"}
      </TableCell>
      <TableCell className="px-1.5 py-1.5 text-right whitespace-normal break-words">
        <div className="font-mono text-[10px] tabular-nums">
          {result?.measuredItemCount ??
            result?.breakdown?.reduce((sum, b) => sum + b.count, 0) ??
            0}
        </div>
        {result?.measuredItemCount !== undefined && (
          <div
            role="status"
            className="mt-0.5 text-[10px] leading-4 text-muted-foreground"
          >
            <div>
              {result.count} accepted · {result.pendingMeasurementCount ?? 0}{" "}
              need review
            </div>
            {result.reviewIncomplete ? <div>Coverage needs review</div> : null}
          </div>
        )}
      </TableCell>
    </>
  );
}

/** Remove and reset are project writes, so reviewers get no row actions menu. */
function TagRowActionsMenu({
  tag,
  isCounting,
  hasScheduleOverrides,
  onRemove,
  onResetScheduleOverrides,
}: {
  tag: TagRecord;
  isCounting: boolean;
  hasScheduleOverrides: boolean;
  onRemove: () => void;
  onResetScheduleOverrides: () => void;
}) {
  const canEdit = useCanEdit();
  if (!canEdit) return null;

  return (
    <RowActionsMenu
      label={`Actions for ${tag.rawValue}`}
      size="compact"
      align="start"
      disabled={isCounting}
      contentClassName="z-[160]"
    >
      <RowAction icon={X} destructive onSelect={onRemove}>
        {tag.isCustom ?? false ? "Delete" : "Remove"}
      </RowAction>
      {hasScheduleOverrides ? (
        <>
          <RowActionSeparator />
          <RowAction icon={RotateCcw} onSelect={onResetScheduleOverrides}>
            Reset schedule data
          </RowAction>
        </>
      ) : null}
    </RowActionsMenu>
  );
}

function TagResultsTable({
  tags,
  hasSchedule,
  selectedResults,
  selectedScheduleHeaders,
  customScheduleHeaders,
  visibleQuantityColumns,
  costColumnsVisible,
  linkedPreviewsByTagId,
  scheduleOverridesByTagId,
  onUpsertScheduleOverride,
  onRemoveScheduleOverrideCells,
  onResetScheduleOverrides,
  expandedBreakdownTagRowIds,
  navigableTagIds,
  onFocusPreviousInstance,
  onFocusNextInstance,
  onFocusMeasurementBreakdownRow,
  onToggleExpandedBreakdownTagRow,
  onOpenScheduleRow,
  isCounting,
  setIncluded,
  removeCustomTag,
  renameCustomTag,
  setUnitCost,
  setCostBasis,
  setColorHex,
}: {
  tags: TagRecord[];
  hasSchedule: boolean;
  selectedResults: CountRow[];
  selectedScheduleHeaders: string[];
  customScheduleHeaders: Set<string>;
  visibleQuantityColumns: Set<ResultQuantityColumn>;
  costColumnsVisible: boolean;
  linkedPreviewsByTagId: Map<string, LinkedSchedulePreviewSummary>;
  scheduleOverridesByTagId: Map<string, ScheduleOverride>;
  onUpsertScheduleOverride: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    cells: Record<string, string>;
  }) => Promise<unknown>;
  onRemoveScheduleOverrideCells: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    keys: string[];
  }) => Promise<unknown>;
  onResetScheduleOverrides: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
  }) => Promise<unknown>;
  expandedBreakdownTagRowIds: Set<string>;
  navigableTagIds: Set<string>;
  onFocusPreviousInstance: (tagId: Id<"takeoffPilotExtractedTags">) => void;
  onFocusNextInstance: (tagId: Id<"takeoffPilotExtractedTags">) => void;
  onFocusMeasurementBreakdownRow: (args: {
    rowKey: string;
    direction: "previous" | "next";
    targets: MeasurementBreakdownTargetRef[];
  }) => void;
  onToggleExpandedBreakdownTagRow: (tagId: string) => void;
  onOpenScheduleRow: (args: {
    tagRawValue: string;
    matchedRow: LinkedSchedulePreviewSummary["matchedRows"][number];
  }) => void;
  isCounting: boolean;
  setIncluded: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    included: boolean;
  }) => Promise<unknown>;
  removeCustomTag: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
  }) => Promise<unknown>;
  renameCustomTag: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    rawValue: string;
  }) => Promise<unknown>;
  setUnitCost: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    unitCost: number | null;
  }) => Promise<unknown>;
  setCostBasis: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    costBasis: TakeoffCostBasis;
  }) => Promise<unknown>;
  setColorHex: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    colorHex: string;
  }) => Promise<unknown>;
}) {
  const resultsByTagId = useMemo(() => {
    const map = new Map<string, CountRow>();
    for (const result of selectedResults) {
      map.set(result.tagId, result);
    }
    return map;
  }, [selectedResults]);
  // Always show Tag Results alphabetically then numerically (P1, P2, TA1…),
  // not schedule extraction / displayOrder order.
  const selectedTags = tags.filter((tag) => tag.included).sort(compareTagsByRawValue);
  const canEdit = useCanEdit();

  const handleRemove = async (tag: TagRecord) => {
    try {
      if (tag.isCustom ?? false) {
        await removeCustomTag({ tagId: tag._id });
        return;
      }

      await setIncluded({ tagId: tag._id, included: false });
    } catch (error) {
      notifyError(error, tag.isCustom ?? false ? "Failed to delete custom line item" : "Failed to remove tag");
      console.error(error);
    }
  };

  if (selectedTags.length === 0) {
    return (
      <div className="flex h-full items-center justify-center overflow-auto p-4 text-sm text-muted-foreground">
        <p>
          {!canEdit
            ? "No tags selected yet."
            : hasSchedule
              ? "No tags selected. Use the dropdown above to add tags from the schedule."
              : "No custom items yet. Use Add Custom Line Item to begin."}
        </p>
      </div>
    );
  }

  const quantityColumns = RESULT_QUANTITY_COLUMNS.filter((column) =>
    visibleQuantityColumns.has(column),
  );
  const tableMinWidthRem =
    RESULT_TABLE_COLUMN_WIDTHS_REM.actions +
    RESULT_TABLE_COLUMN_WIDTHS_REM.color +
    RESULT_TABLE_COLUMN_WIDTHS_REM.tag +
    selectedScheduleHeaders.length * RESULT_TABLE_COLUMN_WIDTHS_REM.schedule +
    quantityColumns.reduce(
      (width, column) =>
        width +
        (column === "count"
          ? RESULT_TABLE_COLUMN_WIDTHS_REM.tagOccurrences +
            RESULT_TABLE_COLUMN_WIDTHS_REM.measuredItems
          : RESULT_TABLE_COLUMN_WIDTHS_REM.quantity),
      0,
    ) +
    (costColumnsVisible
      ? RESULT_TABLE_COLUMN_WIDTHS_REM.unitCost + RESULT_TABLE_COLUMN_WIDTHS_REM.totalCost
      : 0);

  return (
        <Table
          variant="panel"
          className="table-fixed min-w-full"
          style={{ minWidth: `${tableMinWidthRem}rem` }}
        >
          <colgroup>
            <col style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.actions}rem` }} />
            <col style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.color}rem` }} />
            <col />
            {selectedScheduleHeaders.map((header) => (
              <col
                key={`schedule:${header}`}
                style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.schedule}rem` }}
              />
            ))}
            {quantityColumns.map((column) =>
              column === "count" ? (
                <Fragment key={`quantity:${column}`}>
                  <col
                    style={{
                      width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.tagOccurrences}rem`,
                    }}
                  />
                  <col
                    style={{
                      width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.measuredItems}rem`,
                    }}
                  />
                </Fragment>
              ) : (
                <col
                  key={`quantity:${column}`}
                  style={{
                    width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.quantity}rem`,
                  }}
                />
              ),
            )}
            {costColumnsVisible ? (
              <>
                <col style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.unitCost}rem` }} />
                <col style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.totalCost}rem` }} />
              </>
            ) : null}
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead className="h-8 px-1.5 py-1">
                <span className="sr-only">Actions</span>
              </TableHead>
              <TableHead className="h-8 px-1.5 py-1">
                <span className="sr-only">Color</span>
              </TableHead>
              <TableHead className="h-8 px-1.5 py-1 text-[10px] tracking-wider">Tag</TableHead>
              {selectedScheduleHeaders.map((header) => (
                <TableHead
                  key={header}
                  className="h-8 px-1.5 py-1 text-[9px] leading-tight whitespace-normal break-words tracking-wider"
                  title={header}
                >
                  {header}
                </TableHead>
              ))}
              {visibleQuantityColumns.has("count") ? (
                <>
                  <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] leading-snug whitespace-normal break-words">
                    Tag occurrences
                  </TableHead>
                  <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] leading-snug whitespace-normal break-words">
                    Measured items
                  </TableHead>
                </>
              ) : null}
              {visibleQuantityColumns.has("lf") ? (
                <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] tracking-wider">LF</TableHead>
              ) : null}
              {visibleQuantityColumns.has("sqft") ? (
                <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] tracking-wider">SQFT</TableHead>
              ) : null}
              {visibleQuantityColumns.has("cf") ? (
                <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] tracking-wider">CF</TableHead>
              ) : null}
              {costColumnsVisible ? (
                <>
                  <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] tracking-wider">Unit $</TableHead>
                  <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] tracking-wider">Total $</TableHead>
                </>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {selectedTags.map((tag) => {
              const result = resultsByTagId.get(tag._id);
              const linkedPreview = linkedPreviewsByTagId.get(String(tag._id));
              const primaryMatchedRow = linkedPreview?.matchedRows[0] ?? null;
              const isExpanded = expandedBreakdownTagRowIds.has(
                String(tag._id),
              );
              const measurementBreakdownRows =
                groupMeasurementBreakdownByDisplayValue(
                  result?.breakdown,
                  result?.breakdownTargets,
                );
              const hasBreakdownDetails = measurementBreakdownRows.length > 0;
              const totalCostForTag = getTotalCost(result, tag.unitCost, tag.costBasis);
              const canNavigateInstances = navigableTagIds.has(String(tag._id));
              const showTagNavigation = !(tag.isCustom ?? false) || canNavigateInstances;
              const rowAccentColor = tag.colorHex ?? result?.colorHex ?? DEFAULT_TAG_COLOR;

              return (
                <Fragment key={tag._id}>
                  <TableRow
                    className="h-9"
                    style={{ boxShadow: `inset 2px 0 0 ${rowAccentColor}` }}
                  >
                    <TableCell className="px-1.5 py-0 align-middle">
                      <TagRowActionsMenu
                        tag={tag}
                        isCounting={isCounting}
                        hasScheduleOverrides={scheduleOverridesByTagId.has(String(tag._id))}
                        onRemove={() => void handleRemove(tag)}
                        onResetScheduleOverrides={() =>
                          void onResetScheduleOverrides({ tagId: tag._id }).catch((error) => {
                            notifyError(error, "Failed to reset schedule data");
                            console.error(error);
                          })
                        }
                      />
                    </TableCell>
                    <TableCell className="px-1.5 py-0 align-middle">
                      <div className="flex items-center justify-center">
                        <TagColorInput
                          tag={tag}
                          resolvedColorHex={tag.colorHex ?? result?.colorHex ?? DEFAULT_TAG_COLOR}
                          disabled={isCounting}
                          setColorHex={setColorHex}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="px-1.5 py-0.5 align-middle">
                      <div className="flex min-w-0 items-center gap-1.5">
                        {hasBreakdownDetails ? (
                          <RowExpanderButton
                            expanded={isExpanded}
                            onToggle={() => onToggleExpandedBreakdownTagRow(String(tag._id))}
                            label={`Measurement breakdown for ${tag.rawValue}`}
                          />
                        ) : (
                          <RowExpanderSpacer />
                        )}
                        {showTagNavigation ? (
                          <div className="flex w-14 shrink-0 items-center justify-end gap-px">
                            <TagInstanceNavigationButton
                              direction="previous"
                              tagLabel={tag.rawValue}
                              disabled={!canNavigateInstances}
                              onClick={() => onFocusPreviousInstance(tag._id)}
                            />
                            <TagInstanceNavigationButton
                              direction="next"
                              tagLabel={tag.rawValue}
                              disabled={!canNavigateInstances}
                              onClick={() => onFocusNextInstance(tag._id)}
                            />
                          </div>
                        ) : (
                          <span className="w-14 shrink-0" aria-hidden="true" />
                        )}
                        <div className="min-w-0 flex-1">
                          {(tag.isCustom ?? false) ? (
                            <CustomLineItemNameInput
                              tag={tag}
                              disabled={isCounting}
                              renameCustomTag={renameCustomTag}
                            />
                          ) : primaryMatchedRow ? (
                            <button
                              type="button"
                              onClick={() =>
                                onOpenScheduleRow({
                                  tagRawValue: tag.rawValue,
                                  matchedRow: primaryMatchedRow,
                                })
                              }
                              className="block max-w-full truncate text-left text-[11px] font-medium hover:underline"
                              title={tag.rawValue}
                            >
                              {tag.rawValue}
                            </button>
                          ) : (
                            <span className="block truncate text-[11px] font-medium" title={tag.rawValue}>
                              {tag.rawValue}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    {selectedScheduleHeaders.map((header) => {
                      const override = scheduleOverridesByTagId.get(String(tag._id));
                      const overrideValue = override?.cells[header];
                      const baseValue = primaryMatchedRow?.cells[header] ?? "";
                      const isOverridden = overrideValue !== undefined;
                      return (
                        <TableCell
                          key={`${tag._id}:${header}`}
                          className="min-w-0 px-1.5 py-0.5 align-top text-[9px]"
                        >
                          <ScheduleCellInput
                            tagId={tag._id}
                            header={header}
                            baseValue={baseValue}
                            overrideValue={overrideValue}
                            isOverridden={isOverridden}
                            preserveEmptyOverride={customScheduleHeaders.has(header)}
                            onUpsert={onUpsertScheduleOverride}
                            onRemoveCells={onRemoveScheduleOverrideCells}
                          />
                        </TableCell>
                      );
                    })}
                    {visibleQuantityColumns.has("count") ? (
                      <TagMeasurementCounts result={result} />
                    ) : null}
                    {visibleQuantityColumns.has("lf") ? (
                      <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] tabular-nums">
                        {formatMeasurementTotal(MEASUREMENT_TYPES.LF, result?.lf)}
                      </TableCell>
                    ) : null}
                    {visibleQuantityColumns.has("sqft") ? (
                      <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] tabular-nums">
                        {formatMeasurementTotal(MEASUREMENT_TYPES.SQFT, result?.sqft)}
                      </TableCell>
                    ) : null}
                    {visibleQuantityColumns.has("cf") ? (
                      <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] tabular-nums">
                        {formatMeasurementTotal(MEASUREMENT_TYPES.CF, result?.cf)}
                      </TableCell>
                    ) : null}
                    {costColumnsVisible ? (
                      <>
                        <TableCell className="px-1.5 py-0.5 text-right">
                          <UnitCostInput
                            tag={tag}
                            disabled={isCounting}
                            setUnitCost={setUnitCost}
                            setCostBasis={setCostBasis}
                          />
                        </TableCell>
                        <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] font-medium tabular-nums">
                          {totalCostForTag === null ? "—" : `$${formatCurrency(totalCostForTag)}`}
                        </TableCell>
                      </>
                    ) : null}
                  </TableRow>
                  {hasBreakdownDetails && isExpanded
                    ? [
                        ...measurementBreakdownRows.map((row) => {
                          const rowKey = `${tag._id}:measurement:${row.measurementType}:${row.displayValue}`;
                          const canNavigateMeasurement = row.targets.length > 0;
                          return (
                            <TableRow
                              key={rowKey}
                              className="h-8 bg-muted/20"
                              style={{ boxShadow: `inset 2px 0 0 ${rowAccentColor}` }}
                            >
                              <TableCell className="px-1.5 py-0 align-middle" />
                              <TableCell className="px-1.5 py-0 align-middle" />
                              <TableCell className="px-1.5 py-0.5 align-middle text-[9px] text-muted-foreground">
                                <div className="grid min-w-0 grid-cols-[1.5rem_minmax(0,1fr)_2.5rem] items-center gap-1.5">
                                  <span className="size-6" aria-hidden="true" />
                                  <span className="truncate font-mono text-[10px] text-foreground tabular-nums">
                                    {row.displayValue} {row.measurementType}
                                  </span>
                                  <div className="flex items-center gap-px">
                                    <TagInstanceNavigationButton
                                      direction="previous"
                                      tagLabel={`${tag.rawValue} ${row.displayValue} ${row.measurementType}`}
                                      disabled={!canNavigateMeasurement}
                                      onClick={() => {
                                        if (!canNavigateMeasurement) {
                                          return;
                                        }
                                        onFocusMeasurementBreakdownRow({
                                          rowKey,
                                          direction: "previous",
                                          targets: row.targets,
                                        });
                                      }}
                                    />
                                    <TagInstanceNavigationButton
                                      direction="next"
                                      tagLabel={`${tag.rawValue} ${row.displayValue} ${row.measurementType}`}
                                      disabled={!canNavigateMeasurement}
                                      onClick={() => {
                                        if (!canNavigateMeasurement) {
                                          return;
                                        }
                                        onFocusMeasurementBreakdownRow({
                                          rowKey,
                                          direction: "next",
                                          targets: row.targets,
                                        });
                                      }}
                                    />
                                  </div>
                                  <span aria-hidden="true" />
                                </div>
                              </TableCell>
                              {selectedScheduleHeaders.map((header) => (
                                <TableCell
                                  key={`${tag._id}:measurement:${row.measurementType}:${row.displayValue}:${header}`}
                                  className="px-1.5 py-0.5"
                                />
                              ))}
                              {visibleQuantityColumns.has("count") ? (
                                <>
                                  <TableCell className="px-1.5 py-0.5" />
                                  <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] tabular-nums">
                                    {row.count}
                                  </TableCell>
                                </>
                              ) : null}
                              {visibleQuantityColumns.has("lf") ? (
                                <TableCell className="px-1.5 py-0.5" />
                              ) : null}
                              {visibleQuantityColumns.has("sqft") ? (
                                <TableCell className="px-1.5 py-0.5" />
                              ) : null}
                              {visibleQuantityColumns.has("cf") ? (
                                <TableCell className="px-1.5 py-0.5" />
                              ) : null}
                              {costColumnsVisible ? (
                                <>
                                  <TableCell className="px-1.5 py-0.5" />
                                  <TableCell className="px-1.5 py-0.5" />
                                </>
                              ) : null}
                            </TableRow>
                          );
                        }),
                      ]
                    : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
  );
}

function useTakeoffLinkedSchedulePreview(
  jobId: Id<"takeoffPilotJobs"> | undefined,
  overrideHeaders: string[],
  enabled: boolean
) {
  const [selectedScheduleHeaders, setSelectedScheduleHeaders] = useState<string[]>([]);
  const getLinkedSchedulePreview = useAction(
    api.takeoffPilot.tagPreview.getLinkedSchedulePreview
  );
  const { data: linkedSchedulePreview } = useQuery(
    jobId && enabled
      ? {
          queryKey: ["takeoff-linked-schedule-preview", jobId],
          queryFn: async () =>
            keyLinkedSchedulePreviewCells(await getLinkedSchedulePreview({ jobId })),
          staleTime: 30_000,
          refetchOnWindowFocus: false,
        }
      : {
          queryKey: ["takeoff-linked-schedule-preview", "skip"],
          queryFn: async () => undefined,
          enabled: false,
        }
  );

  const availableScheduleColumns = useMemo(
    () =>
      mergeScheduleColumns({
        linkedColumns: linkedSchedulePreview?.availableColumns ?? [],
        overrideHeaders,
      }),
    [linkedSchedulePreview?.availableColumns, overrideHeaders]
  );

  useEffect(() => {
    const availableHeaders = new Set(availableScheduleColumns.map((column) => column.value));
    setSelectedScheduleHeaders((previous) => {
      const next = previous.filter((header) => availableHeaders.has(header));
      return next.length === previous.length ? previous : next;
    });
  }, [availableScheduleColumns]);

  const linkedPreviewsByTagId = useMemo(
    () =>
      new Map(
        (linkedSchedulePreview?.linkedPreviews ?? []).map((preview) => [
          String(preview.tagId),
          preview,
        ] as const)
      ),
    [linkedSchedulePreview]
  );

  const handleSelectScheduleHeader = useCallback((header: string) => {
    setSelectedScheduleHeaders((previous) =>
      previous.includes(header) ? previous : [...previous, header]
    );
  }, []);

  const handleRemoveScheduleHeader = useCallback((header: string) => {
    setSelectedScheduleHeaders((previous) =>
      previous.filter((candidate) => candidate !== header)
    );
  }, []);

  return {
    linkedSchedulePreview,
    availableScheduleColumns,
    linkedPreviewsByTagId,
    selectedScheduleHeaders,
    handleSelectScheduleHeader,
    handleRemoveScheduleHeader,
  };
}

function renderTakeoffPageState(args: {
  jobId: Id<"takeoffPilotJobs"> | undefined;
  detailsError: unknown;
  isPending: boolean;
  details:
    | {
        job: {
          status: string;
          error?: string;
        };
      }
    | undefined;
  status: ReturnType<typeof useJobStatus>;
  countsError: unknown;
  hasMeasurementTotalsError: boolean;
  measurementTotalsError: unknown;
}) {
  if (!args.jobId) return <SpinnerState className="h-full" />;
  if (args.detailsError) return <DetailsErrorState />;
  if (args.isPending || !args.details) return <SpinnerState className="h-full" />;
  if (args.status.isCopyingRevision) return <CopyingRevisionState />;
  if (args.status.isExtracting) return <ExtractingState />;
  if (args.details.job.error) {
    return (
      <ErrorState
        title="Tag Counting Failed"
        message={args.details.job.error}
        destructive
      />
    );
  }
  if (args.countsError) return <CountsErrorState />;
  if (args.hasMeasurementTotalsError) {
    return (
      <ErrorState
        title="Measurement totals failed"
        message={
          args.measurementTotalsError instanceof Error
            ? args.measurementTotalsError.message
            : "Unable to load measurement totals"
        }
        destructive
      />
    );
  }
  return null;
}

const SCHEDULE_SOURCE_SUFFIX = " (Schedule)";

/**
 * The linked schedule's revision name. The job record only stores the revision ID, but the
 * viewer's schedule source is labelled `"<revision name> (Schedule)"` by the backend.
 */
function getScheduleRevisionName(
  sources: ReadonlyArray<{ sourceType: string; displayName: string }> | undefined
) {
  const label = sources?.find((source) => source.sourceType === "schedule")?.displayName;
  if (!label) {
    return null;
  }
  return label.endsWith(SCHEDULE_SOURCE_SUFFIX)
    ? label.slice(0, -SCHEDULE_SOURCE_SUFFIX.length)
    : label;
}

/**
 * Viewer top bar subtitle, matching FieldPilot: the takeoff revision plus the schedule revision
 * name, or "No schedule" for scheduleless jobs.
 */
function getTakeoffViewerSubtitle(
  revisionNumber: number | undefined,
  hasSchedule: boolean,
  scheduleRevisionName: string | null
) {
  const parts = [
    revisionNumber !== undefined ? `R${revisionNumber}` : null,
    hasSchedule ? scheduleRevisionName : "No schedule",
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/**
 * Viewer chrome for states without a document (loading, errors, missing plan file), so the exit
 * control is always reachable — TopNav is hidden on this route.
 */
function TakeoffViewerStateShell({
  title,
  subtitle,
  readOnly,
  onBack,
  children,
}: {
  title: string;
  subtitle?: string;
  readOnly: boolean;
  onBack: () => void;
  children: ReactNode;
}) {
  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-background">
      <ViewerTopBar
        back={<ViewerBackButton label="Back to TakeoffPilot" onBack={onBack} />}
        title={title}
        subtitle={subtitle}
        readOnly={readOnly}
      />
      <div className="relative min-h-0 flex-1 overflow-auto">{children}</div>
    </div>
  );
}

function renderTakeoffResultsViewer(args: {
  jobHistory: TakeoffJobHistory;
  activeSource: ViewerSource | undefined;
  jobId: Id<"takeoffPilotJobs">;
  viewerRef: RefObject<TakeoffResultsViewerHandle | null>;
  pendingMeasurementFocusRef: RefObject<MeasurementBreakdownTargetRef | null>;
  viewerTags: ReturnType<typeof useComputedValues>["viewerTags"];
  onNavigableTagIdsChange: (tagIds: string[]) => void;
  viewerSources: ViewerSource[];
  onSelectPlanDocumentId: (value: string) => void;
  /** Every source, built or not, listed while the default one is prepared. */
  preparingSources: ViewerSource[];
  jobName: string;
  jobSubtitle?: string;
  aiReviewContainer: HTMLDivElement | null;
  aiReviewOpen: boolean;
  aiReviewLocation: (AiReviewLocation & { sequence: number }) | null;
  onNavigateAiReview: (location: AiReviewLocation) => void;
  onToggleAiReview: () => void;
  readOnly: boolean;
  onClose: () => void;
  onExport: () => void;
  isExportInProgress: boolean;
  onCreateCustomLineItem: (args: {
    rawValue: string;
    unitCost: number | null;
  }) => Promise<Id<"takeoffPilotExtractedTags"> | void>;
  bottomPanelHeight: number;
}) {
  if (!args.activeSource?.fileUrl) {
    return (
      <TakeoffViewerStateShell
        title={args.jobName}
        subtitle={args.jobSubtitle}
        readOnly={args.readOnly}
        onBack={args.onClose}
      >
        <PreparingDocumentsState
          sources={args.preparingSources}
          onOpen={args.onSelectPlanDocumentId}
          canRetry={!args.readOnly}
        />
      </TakeoffViewerStateShell>
    );
  }

  return (
    <TakeoffResultsViewer
      jobHistory={args.jobHistory}
      documentScopeKey={args.activeSource.sourceId}
      ref={args.viewerRef}
      sharedPendingFocusRef={args.pendingMeasurementFocusRef}
      jobId={args.jobId}
      planDocumentId={args.activeSource.planDocumentId}
      planRevisionId={args.activeSource.revisionId}
      planFilename={args.activeSource.filename}
      planFileUrl={args.activeSource.fileUrl}
      planProjectId={args.activeSource.projectId}
      planSourceDocumentId={args.activeSource.documentId}
      documentIntelligenceRunId={args.activeSource.documentIntelligenceRunId}
      persistedTextLocked={args.activeSource.measurementTextLocked}
      persistedTextFontSize={args.activeSource.measurementTextFontSize}
      tags={args.viewerTags}
      isViewOnly={args.activeSource.sourceType === "schedule"}
      onNavigableTagIdsChange={args.onNavigableTagIdsChange}
      viewerSources={args.viewerSources}
      selectedPlanDocumentId={args.activeSource.sourceId}
      onSelectPlanDocumentId={args.onSelectPlanDocumentId}
      jobName={args.jobName}
      jobSubtitle={args.jobSubtitle}
      aiReviewContainer={args.aiReviewContainer}
      aiReviewOpen={args.aiReviewOpen}
      aiReviewLocation={args.aiReviewLocation}
      onNavigateAiReview={args.onNavigateAiReview}
      onToggleAiReview={args.onToggleAiReview}
      onClose={args.onClose}
      onExport={args.onExport}
      isExportInProgress={args.isExportInProgress}
      onCreateCustomLineItem={args.onCreateCustomLineItem}
      bottomPanelHeight={args.bottomPanelHeight}
    />
  );
}

export function TakeoffPilotJobPage() {
  const jobId = useRouteParam("jobId") as Id<"takeoffPilotJobs"> | undefined;
  const retryPreparation = useMutation(api.takeoffPilot.aiWorkflow.retryFailedPreparation);
  const canEdit = useCanEdit();
  const [retrying, setRetrying] = useState(false);
  const { data: details } = useQuery(
    convexQuery(api.takeoffPilot.jobs.getWorkspaceDetails, jobId ? { jobId } : "skip")
  );

  const { data: aiSetup } = useQuery(convexQuery(api.takeoffPilot.aiWorkflow.get,
    AI_COUNTS_ENABLED && details?.job.aiTakeoffRequested && jobId ? { jobId } : "skip"));
  const navigate = useAppNavigate();
  const awaitingSetup = AI_COUNTS_ENABLED && details?.job.aiTakeoffRequested && details.job.status !== "failed" && aiSetup === undefined;
  const needsSetup = AI_COUNTS_ENABLED && isAiSetupPending(details, aiSetup);
  useEffect(() => {
    if (needsSetup && jobId) navigate({ to: "/takeoffPilot/$jobId/setup", params: { jobId }, title: details?.job.name, replace: true });
  }, [needsSetup, jobId, navigate, details?.job.name]);
  if (jobId && details?.job.status === "failed" && aiSetup?.canRetryPreparation && AI_COUNTS_ENABLED && details.job.aiTakeoffRequested) {
    return <ErrorState title="Tag Counting Failed" message={details.job.error ?? "Preparation failed"} destructive action={canEdit ?
      <Button className="mt-4" disabled={retrying} onClick={async () => {
        setRetrying(true);
        try { await retryPreparation({ jobId, version: aiSetup.version }); }
        catch (error) { toast.error(error instanceof Error ? error.message : "Unable to retry preparation"); }
        finally { setRetrying(false); }
      }}>Retry Failed Preparation</Button> : undefined
    } />;
  }
  if (needsSetup || awaitingSetup) return <div role="status" className="p-6 text-sm text-muted-foreground">Loading AI takeoff…</div>;

  return (
    <ActionLogProvider
      projectId={details?.job.projectId ?? null}
      pilot="TakeoffPilot"
      targetKind="takeoffPilotJob"
      targetId={jobId}
    >
      <TakeoffPilotJobPageContent aiWorkflowStatus={aiSetup?.started ? aiSetup.status : undefined} />
    </ActionLogProvider>
  );
}

// eslint-disable-next-line complexity
function TakeoffPilotJobPageContent({ aiWorkflowStatus }: { aiWorkflowStatus?: string }) {
  const [aiReviewOpen, setAiReviewOpen] = useState(Boolean(aiWorkflowStatus));
  const [aiReviewContainer, setAiReviewContainer] = useState<HTMLDivElement | null>(null);
  const canEdit = useCanEdit();
  const jobId = useRouteParam("jobId") as Id<"takeoffPilotJobs"> | undefined;
  const [aiReviewLocation, setAiReviewLocation] = useState<(AiReviewLocation & { sequence: number }) | null>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`takeoff-ai-review:${jobId}`) ?? "null");
      return saved && typeof saved.tagId === "string" && typeof saved.planDocumentId === "string" && Number.isSafeInteger(saved.pageNumber) && saved.pageNumber > 0 ? { ...saved, sequence: 0 } : null;
    } catch { return null; }
  });
  const returnToRevPilotComparisonIdFromParams = useRouteParam("returnToRevPilotComparisonId");
  const routeSearch = useSearch({ strict: false }) as Record<string, string | undefined>;
  const returnToRevPilotComparisonId =
    returnToRevPilotComparisonIdFromParams ?? routeSearch.returnToRevPilotComparisonId;
  const queuePlanAnalysis = useMutation(api.takeoffPilot.jobs.queuePlanAnalysis);
  const convex = useConvex();
  const setIncluded = useLoggedMutation(api.takeoffPilot.tags.setIncluded);
  const setIncludedBatch = useLoggedMutation(api.takeoffPilot.tags.setIncludedBatch);
  const createCustomTag = useLoggedMutation(api.takeoffPilot.tags.createCustom);
  const createCustomTagsFromDocumentTags = useLoggedMutation(api.takeoffPilot.tags.createCustomFromDocumentTags);
  const removeCustomTag = useLoggedMutation(api.takeoffPilot.tags.removeCustom);
  const renameCustomTag = useLoggedMutation(api.takeoffPilot.tags.renameCustom);
  const setUnitCost = useLoggedMutation(api.takeoffPilot.tags.setUnitCost);
  const setCostBasis = useLoggedMutation(api.takeoffPilot.tags.setCostBasis);
  const setTagColorHex = useLoggedMutation(api.takeoffPilot.tags.setColorHex);
  const generatePdfExport = useMutation(api.takeoffPilot.jobs.generatePdfExport);
  const generateWorkbookExport = useMutation(api.takeoffPilot.jobs.generateWorkbookExport);
  const { overrides: scheduleOverrides } = useScheduleOverrides(jobId);
  const upsertScheduleOverride = useUpsertScheduleOverride();
  const addScheduleOverrideColumn = useAddScheduleOverrideColumn();
  const removeScheduleOverrideCells = useRemoveScheduleOverrideCells();
  const resetScheduleOverride = useResetScheduleOverride();
  const scheduleOverridesByTagId = useMemo(
    () =>
      new Map(
        scheduleOverrides.map((override) => [String(override.tagId), override] as const)
      ),
    [scheduleOverrides]
  );
  const overrideHeaders = useMemo(
    () =>
      Array.from(
        new Set(scheduleOverrides.flatMap((override) => Object.keys(override.cells)))
      ).sort((left, right) => left.localeCompare(right)),
    [scheduleOverrides]
  );
  const [optimisticScheduleHeaders, setOptimisticScheduleHeaders] = useState<string[]>([]);
  const [selectedPlanDocumentId, setSelectedPlanDocumentId] = useState<string>("");
  const handleNavigateAiReview = useCallback((location: AiReviewLocation) => {
    setSelectedPlanDocumentId(location.planDocumentId);
    setAiReviewLocation(previous => ({ ...location, sequence: (previous?.sequence ?? 0) + 1 }));
    try { localStorage.setItem(`takeoff-ai-review:${jobId}`, JSON.stringify(location)); } catch { /* Review remains usable when browser storage is unavailable. */ }
  }, [jobId]);
  useEffect(() => { if (aiReviewLocation) setSelectedPlanDocumentId(aiReviewLocation.planDocumentId); }, [aiReviewLocation]);
  const [includeScheduleInPdf, setIncludeScheduleInPdf] = useState(true);
  const [includeTagsInPdf, setIncludeTagsInPdf] = useState(true);
  const [includeMeasurementsInPdf, setIncludeMeasurementsInPdf] = useState(true);
  const [pdfReportMode, setPdfReportMode] = useState<"executive_summary" | "full">("full");
  const [includeCostsInExport, setIncludeCostsInExport] = useState(true);
  const [pdfExportIncludeColumns, setPdfExportIncludeColumns] = useState<string[]>([
    ...PDF_EXPORT_MEASUREMENT_COLUMNS,
  ]);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>("pdf");
  const [isSubmittingExport, setIsSubmittingExport] = useState(false);
  // CSV export is built in the browser and stays open; PDF and Excel generation do not.
  useOnEditAccessLost(() => setExportFormat("csv"));
  const [isQueueingPdfExport, setIsQueueingPdfExport] = useState(false);
  const [isPdfExportOptimisticPending, setIsPdfExportOptimisticPending] = useState(false);
  const [autoDownloadPdfArtifactId, setAutoDownloadPdfArtifactId] = useState<string | null>(null);
  const autoDownloadPdfInFlightRef = useRef<string | null>(null);
  // The name typed for a queued export, so the auto-download doesn't wait on the saved-name query.
  const autoDownloadNameRef = useRef<string | null>(null);
  const [isQueueingWorkbookExport, setIsQueueingWorkbookExport] = useState(false);
  const [isWorkbookExportOptimisticPending, setIsWorkbookExportOptimisticPending] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [visibleQuantityColumns, setVisibleQuantityColumns] =
    useState<ResultQuantityColumn[]>([...RESULT_QUANTITY_COLUMNS]);
  const [innerHeight, setInnerHeight] = useState(() => typeof window !== "undefined" ? window.innerHeight : 800);
  const [drawingWorkspace, setDrawingWorkspace] = useState<HTMLDivElement | null>(null);
  const maxResultsHeight = Math.min(getBottomPanelMaxHeight(innerHeight), Math.max(150, innerHeight - 240));
  const [panelHeight, setPanelHeight] = useState(() =>
    Math.min(300, getBottomPanelMaxHeight(innerHeight))
  );
  const [selectedScheduleRow, setSelectedScheduleRow] = useState<{
    tagRawValue: string;
    matchedRow: LinkedSchedulePreviewSummary["matchedRows"][number];
  } | null>(null);
  const [expandedBreakdownTagRowIds, setExpandedBreakdownTagRowIds] = useState<
    Set<string>
  >(new Set());
  const takeoffResultsViewerRef = useRef<TakeoffResultsViewerHandle | null>(
    null,
  );
  const pendingMeasurementFocusRef = useMemo(() => ({ current: null as MeasurementBreakdownTargetRef | null }), [jobId]);
  const pdfExportJobContextRef = useRef<{
    jobId: string;
    hasSchedule: boolean;
  } | null>(null);
  const [navigableTagIds, setNavigableTagIds] = useState<Set<string>>(
    new Set(),
  );
  const [costColumnsVisible, setCostColumnsVisible] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }

    try {
      const stored = window.localStorage.getItem(COST_COLUMNS_VISIBLE_STORAGE_KEY);
      if (stored === "false") {
        return false;
      }
      if (stored === "true") {
        return true;
      }
      return true;
    } catch {
      return true;
    }
  });

  const [pendingSelections, setPendingSelections] = useState<Map<string, boolean>>(new Map());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const flushInFlightRef = useRef<Promise<boolean> | null>(null);
  // Selections a demoted member hadn't flushed yet would be rejected writes.
  useOnEditAccessLost(() => setPendingSelections(new Map()));

  useEffect(() => {
    const handleResize = () => {
      const newHeight = drawingWorkspace?.clientHeight || window.innerHeight;
      setInnerHeight(newHeight);
      const newMaxHeight = Math.min(getBottomPanelMaxHeight(newHeight), Math.max(150, newHeight - 240));
      setPanelHeight((prev) => Math.min(prev, newMaxHeight));
    };
    handleResize();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(handleResize) : null;
    if (drawingWorkspace) observer?.observe(drawingWorkspace);
    window.addEventListener("resize", handleResize);
    return () => { observer?.disconnect(); window.removeEventListener("resize", handleResize); };
  }, [drawingWorkspace]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(
        COST_COLUMNS_VISIBLE_STORAGE_KEY,
        String(costColumnsVisible)
      );
    } catch {
      // Ignore storage write failures and keep the in-memory preference.
    }
  }, [costColumnsVisible]);

  const handleDropdownSelect = (tagId: string) => {
    setPendingSelections((prev) => {
      const next = new Map(prev);
      next.set(tagId, true);
      return next;
    });
  };

  const handleDropdownRemove = (tagId: string) => {
    setPendingSelections((prev) => {
      const next = new Map(prev);
      next.set(tagId, false);
      return next;
    });
  };
  const handleSelectQuantityColumn = useCallback((column: string) => {
    if (!RESULT_QUANTITY_COLUMNS.includes(column as ResultQuantityColumn)) {
      return;
    }

    setVisibleQuantityColumns((previous) =>
      previous.includes(column as ResultQuantityColumn)
        ? previous
        : [...previous, column as ResultQuantityColumn]
    );
  }, []);

  const handleRemoveQuantityColumn = useCallback((column: string) => {
    setVisibleQuantityColumns((previous) =>
      previous.filter((candidate) => candidate !== column)
    );
  }, []);

  const flushPendingSelections = useCallback(async () => {
    if (flushInFlightRef.current) {
      return await flushInFlightRef.current;
    }

    if (!canEdit || pendingSelections.size === 0) {
      return true;
    }

    const sentSelections = new Map(pendingSelections);
    const selections = Array.from(sentSelections.entries()).map(([tagId, included]) => ({
      tagId: tagId as Id<"takeoffPilotExtractedTags">,
      included,
    }));

    let flushPromise: Promise<boolean> | null = null;
    flushPromise = (async () => {
      try {
        await setIncludedBatch({ selections });
        setPendingSelections((prev) => {
          const next = new Map(prev);
          for (const [id, included] of sentSelections) {
            if (next.get(id) === included) {
              next.delete(id);
            }
          }
          return next;
        });
        return true;
      } catch (error) {
        notifyError(error, "Failed to update tag selections");
        console.error(error);
        return false;
      } finally {
        if (flushInFlightRef.current === flushPromise) {
          flushInFlightRef.current = null;
        }
      }
    })();

    flushInFlightRef.current = flushPromise;
    return await flushPromise;
  }, [canEdit, pendingSelections, setIncludedBatch]);

  const handleDropdownOpenChange = useCallback(async (open: boolean) => {
    setIsDropdownOpen(open);

    if (!open) {
      return await flushPendingSelections();
    }
    return true;
  }, [flushPendingSelections]);

  const handleHideResults = useCallback(async () => {
    const flushed = await handleDropdownOpenChange(false);
    if (flushed === false) {
      return;
    }
    setIsPanelOpen(false);
  }, [handleDropdownOpenChange]);

  const { data: details, isPending, error: detailsError } = useQuery(
    convexQuery(api.takeoffPilot.jobs.getWorkspaceDetails, jobId ? { jobId } : "skip")
  );
  const { data: aiExport } = useQuery(convexQuery(api.takeoffPilot.jobs.aiCountExportReady,
    AI_COUNTS_ENABLED && jobId ? { jobId } : "skip"));
  const aiExportReady = !AI_COUNTS_ENABLED || aiExport?.ready === true;
  const includedTagIds = useMemo(
    () => (details?.tags ?? []).filter((tag) => tag.included).map((tag) => tag._id),
    [details?.tags]
  );
  const { data: counts, isPending: isCountsPending, error: countsError } = useQuery(
    convexQuery(
      api.takeoffPilot.counts.listSummariesByTag,
      jobId ? { jobId, tagIds: includedTagIds } : "skip"
    )
  );
  const {
    data: measurementTotals,
    isPending: isMeasurementTotalsPending,
    error: measurementTotalsError,
  } = useQuery(
    convexQuery(
      api.takeoffPilot.measurements.listTotalsByTag,
      jobId ? { jobId, tagIds: includedTagIds } : "skip"
    )
  );
  const { data: pdfExportDownloadUrl, refetch: refetchPdfExportDownloadUrl } = useQuery(
    convexQuery(api.takeoffPilot.jobs.getPdfExportDownloadUrl, jobId ? { jobId } : "skip")
  );
  const { data: workbookExportDownloadUrl, refetch: refetchWorkbookExportDownloadUrl } = useQuery(
    convexQuery(api.takeoffPilot.jobs.getWorkbookExportDownloadUrl, jobId ? { jobId } : "skip")
  );
  const { data: viewerSources } = useQuery(
    convexQuery(api.takeoffPilot.jobs.listPlanDocumentViewerSources, jobId ? { jobId } : "skip")
  );
  const scheduleHeadersFromOverrides = useMemo(
    () =>
      Array.from(new Set([...overrideHeaders, ...optimisticScheduleHeaders])).sort((left, right) =>
        left.localeCompare(right)
      ),
    [optimisticScheduleHeaders, overrideHeaders]
  );
  const hasSchedule = Boolean(details?.job.scheduleRevisionId);
  const viewerTitle = details?.job.name ?? "TakeoffPilot";
  const baseViewerSubtitle = details
    ? getTakeoffViewerSubtitle(
        details.job.revisionNumber,
        hasSchedule,
        getScheduleRevisionName(viewerSources)
      )
    : undefined;
  const aiProgressLabel = aiWorkflowStatus ? ({ preparing: "Preparing AI takeoff", running: "AI takeoff running", ready_for_review: "AI ready to review", needs_attention: "AI takeoff needs attention" } as Record<string, string>)[aiWorkflowStatus] : undefined;
  const viewerSubtitle = [baseViewerSubtitle, aiProgressLabel].filter(Boolean).join(" · ") || undefined;

  const {
    availableScheduleColumns,
    linkedPreviewsByTagId,
    selectedScheduleHeaders,
    handleSelectScheduleHeader,
    handleRemoveScheduleHeader,
  } = useTakeoffLinkedSchedulePreview(jobId, scheduleHeadersFromOverrides, hasSchedule);
  const customScheduleHeaders = useMemo(
    () => new Set(availableScheduleColumns.filter((column) => column.isCustom).map((column) => column.value)),
    [availableScheduleColumns]
  );
  const visibleQuantityColumnSet = useMemo(
    () => new Set(visibleQuantityColumns),
    [visibleQuantityColumns]
  );
  const pdfExportColumnSections = useMemo<MultiSelectSection[]>(
    () => [
      {
        id: "takeoff-columns",
        label: "Takeoff Columns",
        options: PDF_EXPORT_MEASUREMENT_COLUMNS.map((column) => ({
          value: column,
          label: column,
        })),
      },
      {
        id: "schedule-columns",
        label: hasSchedule ? "Schedule Columns" : "Custom Columns",
        options: availableScheduleColumns.map((column) => ({
          value: encodePdfExportScheduleColumn(column.value),
          label: column.label,
        })),
      },
    ].filter((section) => section.options.length > 0),
    [availableScheduleColumns, hasSchedule]
  );
  const availablePdfExportColumns = useMemo(
    () => new Set(pdfExportColumnSections.flatMap((section) => section.options.map((option) => option.value))),
    [pdfExportColumnSections]
  );

  const tags = details?.tags ?? [];
  const tables = details?.tables ?? [];
  const countRows = counts as CountRow[] | undefined;
  const isMeasurementTotalsInitialLoad = isMeasurementTotalsPending && !measurementTotals;
  const hasMeasurementTotalsError = !measurementTotals && !!measurementTotalsError;

  const computed = useComputedValues(
    tags,
    countRows,
    measurementTotals,
    viewerSources as ViewerSource[] | undefined
  );
  const resolvedViewerSources = useResolvedViewerSources({
    jobId,
    viewerSources: viewerSources as ViewerSource[] | undefined,
    orderedViewerSources: computed.orderedViewerSources,
    selectedPlanDocumentId,
    setSelectedPlanDocumentId,
  });
  const status = useJobStatus(details?.job);
  const pdfExport = details?.pdfExport;
  const workbookExport = details?.workbookExport;
  const exportStatus = toProcessingStateWithIdle(pdfExport?.status);
  const workbookExportStatus = toProcessingStateWithIdle(workbookExport?.status);
  const pdfExportOptimisticStartStatusRef = useRef<ProcessingStateWithIdle>("idle");
  const workbookExportOptimisticStartStatusRef = useRef<ProcessingStateWithIdle>("idle");
  const isPdfExportServerInProgress =
    exportStatus === GLOBAL_PROCESSING_PENDING ||
    exportStatus === GLOBAL_PROCESSING_PROCESSING;
  const isPdfExportInProgress =
    isQueueingPdfExport || isPdfExportOptimisticPending || isPdfExportServerInProgress;
  const isWorkbookExportServerInProgress =
    workbookExportStatus === GLOBAL_PROCESSING_PENDING ||
    workbookExportStatus === GLOBAL_PROCESSING_PROCESSING;
  const isWorkbookExportInProgress =
    isQueueingWorkbookExport || isWorkbookExportOptimisticPending || isWorkbookExportServerInProgress;
  const canExportPdf = status.isResults && !isPdfExportInProgress && aiExportReady;
  const canExportWorkbook = status.isResults && !isWorkbookExportInProgress && aiExportReady;
  const canDownloadPdf =
    !isPdfExportInProgress && aiExportReady &&
    Boolean(pdfExportDownloadUrl);
  const canDownloadWorkbook =
    !isWorkbookExportInProgress && aiExportReady &&
    Boolean(workbookExportDownloadUrl);
  const exportFilenames = useExportFilenames(jobId ? { kind: "takeoffPilotJob", id: jobId } : null);
  const takeoffTarget = exportTarget("takeoff_csv", {}, {
    projectName: details?.project?.name,
    jobName: details?.job?.name,
  });
  const takeoffExportName = exportFilenames.resolve(takeoffTarget.key, takeoffTarget.defaultName);
  const canDownloadCsv =
    aiExportReady &&
    computed.selectedCount > 0 &&
    status.isResults &&
    !isCountsPending &&
    !!countRows &&
    !isMeasurementTotalsInitialLoad &&
    !hasMeasurementTotalsError;

  useEffect(() => {
    const currentJobId = details?.job._id ? String(details.job._id) : "";
    const previousContext = pdfExportJobContextRef.current;
    if (
      !currentJobId ||
      (previousContext?.jobId === currentJobId && previousContext.hasSchedule === hasSchedule)
    ) {
      return;
    }

    pdfExportJobContextRef.current = { jobId: currentJobId, hasSchedule };
    setIncludeScheduleInPdf(hasSchedule);
  }, [details?.job._id, hasSchedule]);

  useEffect(() => {
    if (optimisticScheduleHeaders.length === 0) {
      return;
    }

    const persistedHeaders = new Set(overrideHeaders);
    setOptimisticScheduleHeaders((previous) => {
      const next = previous.filter((header) => !persistedHeaders.has(header));
      return next.length === previous.length ? previous : next;
    });
  }, [optimisticScheduleHeaders.length, overrideHeaders]);

  useEffect(() => {
    const validTagIds = new Set(tags.map((tag) => String(tag._id)));
    setExpandedBreakdownTagRowIds((previous) => {
      const next = new Set(Array.from(previous).filter((tagId) => validTagIds.has(tagId)));
      return next.size === previous.size ? previous : next;
    });
  }, [tags]);

  const handleToggleExpandedBreakdownTagRow = useCallback((tagId: string) => {
    setExpandedBreakdownTagRowIds((previous) => {
      const next = new Set(previous);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setPdfExportIncludeColumns((previous) => {
      const next = previous.filter((column) => availablePdfExportColumns.has(column));
      return next.length === previous.length && next.every((column, index) => column === previous[index])
        ? previous
        : next;
    });
  }, [availablePdfExportColumns]);

  const handleSelectPdfExportColumn = useCallback((column: string) => {
    setPdfExportIncludeColumns((previous) =>
      previous.includes(column) ? previous : [...previous, column]
    );
  }, []);

  const handleRemovePdfExportColumn = useCallback((column: string) => {
    setPdfExportIncludeColumns((previous) => previous.filter((candidate) => candidate !== column));
  }, []);

  const handleSelectAllPdfExportColumns = useCallback(
    (sectionId: string) => {
      const values =
        sectionId === "takeoff-columns"
          ? [...PDF_EXPORT_MEASUREMENT_COLUMNS]
          : availableScheduleColumns.map((column) => encodePdfExportScheduleColumn(column.value));
      setPdfExportIncludeColumns((previous) =>
        Array.from(new Set([...previous, ...values.filter((value) => availablePdfExportColumns.has(value))]))
      );
    },
    [availablePdfExportColumns, availableScheduleColumns]
  );

  const handleDeselectAllPdfExportColumns = useCallback(
    (sectionId: string) => {
      const values = new Set(
        sectionId === "takeoff-columns"
          ? [...PDF_EXPORT_MEASUREMENT_COLUMNS]
          : availableScheduleColumns.map((column) => encodePdfExportScheduleColumn(column.value))
      );
      setPdfExportIncludeColumns((previous) => previous.filter((column) => !values.has(column)));
    },
    [availableScheduleColumns]
  );

  useTakeoffSelectionRecount({
    // Selection changes come from editors' writes; a reviewer's view never queues a recount.
    detailsLoaded: !!details && canEdit,
    showWorkspace: status.showWorkspace,
    selectedTagSignature: computed.selectedTagSignature,
    isCounting: status.isCounting,
    isSelecting: status.isSelecting,
    jobId,
    queuePlanAnalysis,
  });

  const allViewerSources = (viewerSources as ViewerSource[] | undefined) ?? resolvedViewerSources;
  const activeSource = resolveActiveViewerSource(allViewerSources, selectedPlanDocumentId);

  const pageState = renderTakeoffPageState({
    jobId,
    detailsError,
    isPending,
    details,
    status,
    countsError,
    hasMeasurementTotalsError,
    measurementTotalsError,
  });
  const jobHistory = useTakeoffJobHistory(jobId, !pageState && status.showWorkspace && !!activeSource?.fileUrl && activeSource.sourceType !== "schedule" && canEdit);
  const handleDownloadCsv = async (fileName: string) => {
    if (!aiExportReady) {
      toast.error(aiExport?.reason ?? "Wait for AI review readiness");
      return false;
    }
    if (AI_COUNTS_ENABLED && jobId) {
      try {
        const current = await convex.query(api.takeoffPilot.jobs.aiCountExportReady, { jobId });
        if (!current.ready) {
          toast.error(current.reason ?? "Finish AI review before export");
          return false;
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not check AI review readiness");
        return false;
      }
    }
    const error = downloadTakeoffCsv({
      filename: withExportExtension(fileName, "csv"),
      isCountsPending,
      countRows,
      isMeasurementTotalsInitialLoad,
      hasMeasurementTotalsError,
      selectedTagsForExport: computed.selectedTagsForExport,
      selectedResults: computed.selectedResults,
      includeColumns: pdfExportIncludeColumns,
      reportMode: pdfReportMode,
      includeCosts: includeCostsInExport,
      linkedPreviewsByTagId,
      scheduleOverridesByTagId,
    });
    if (error) {
      toast.error(error);
      return false;
    }
    return true;
  };

  const handleDownloadExcel = async (fileName: string) => {
    if (!jobId || !canDownloadWorkbook) {
      return false;
    }

    try {
      const { data: refreshedUrl } = await refetchWorkbookExportDownloadUrl();
      const downloadUrl = refreshedUrl ?? workbookExportDownloadUrl;
      if (!downloadUrl) {
        throw new Error("Failed to get download URL");
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = withExportExtension(fileName, "xlsx");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Download started");
      return true;
    } catch (error) {
      notifyError(error, "Failed to download Excel workbook");
      console.error(error);
      return false;
    }
  };

  const handleDownloadPdf = useCallback(async (fileName?: string) => {
    if (!jobId || !canDownloadPdf) {
      return false;
    }

    try {
      const { data: refreshedUrl } = await refetchPdfExportDownloadUrl();
      const downloadUrl = refreshedUrl ?? pdfExportDownloadUrl;
      if (!downloadUrl) {
        throw new Error("Failed to get download URL");
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = withExportExtension(fileName ?? takeoffExportName, "pdf");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Download started");
      return true;
    } catch (error) {
      notifyError(error, "Failed to download PDF export");
      console.error(error);
      return false;
    }
  }, [
    canDownloadPdf,
    takeoffExportName,
    jobId,
    pdfExportDownloadUrl,
    refetchPdfExportDownloadUrl,
  ]);

  useEffect(() => {
    if (!autoDownloadPdfArtifactId) return;
    const currentArtifactId = pdfExport?.artifactId ? String(pdfExport.artifactId) : null;
    if (currentArtifactId !== autoDownloadPdfArtifactId) return;

    if (exportStatus === GLOBAL_PROCESSING_FAILED) {
      toast.error(pdfExport?.error || "PDF export failed");
      setAutoDownloadPdfArtifactId(null);
      return;
    }

    if (exportStatus !== GLOBAL_PROCESSING_COMPLETED || !pdfExportDownloadUrl || !canDownloadPdf) return;
    if (autoDownloadPdfInFlightRef.current === autoDownloadPdfArtifactId) return;

    autoDownloadPdfInFlightRef.current = autoDownloadPdfArtifactId;
    void (async () => {
      try {
        await handleDownloadPdf(autoDownloadNameRef.current ?? undefined);
      } catch (error) {
        console.error("Failed to auto-download PDF export:", error);
      } finally {
        autoDownloadPdfInFlightRef.current = null;
        setAutoDownloadPdfArtifactId(null);
      }
    })();
  }, [
    autoDownloadPdfArtifactId,
    exportStatus,
    canDownloadPdf,
    handleDownloadPdf,
    pdfExport?.artifactId,
    pdfExport?.error,
    pdfExportDownloadUrl,
  ]);

  const handleQueuePdfExport = async (fileName: string) => {
    if (!jobId || isPdfExportInProgress) return null;

    pdfExportOptimisticStartStatusRef.current = exportStatus;
    setIsPdfExportOptimisticPending(true);
    setIsQueueingPdfExport(true);
    try {
      const result = await generatePdfExport({
        jobId,
        includeSchedule: hasSchedule && includeScheduleInPdf,
        includeTags: includeTagsInPdf,
        includeMeasurements: includeMeasurementsInPdf,
        includeColumns: pdfExportIncludeColumns,
        reportMode: pdfReportMode,
      });
      autoDownloadNameRef.current = fileName;
      setAutoDownloadPdfArtifactId(String(result.artifactId));
      toast.success("Preparing PDF export. Download will start when ready.");
      return result.artifactId;
    } catch (error) {
      setIsPdfExportOptimisticPending(false);
      notifyError(error, "Failed to queue PDF export");
      console.error(error);
      return null;
    } finally {
      setIsQueueingPdfExport(false);
    }
  };

  const handleQueueWorkbookExport = async () => {
    if (!jobId || isWorkbookExportInProgress) return null;

    workbookExportOptimisticStartStatusRef.current = workbookExportStatus;
    setIsWorkbookExportOptimisticPending(true);
    setIsQueueingWorkbookExport(true);
    try {
      const result = await generateWorkbookExport({
        jobId,
        includeColumns: pdfExportIncludeColumns,
        reportMode: pdfReportMode,
        includeCosts: includeCostsInExport,
      });
      toast.success("Preparing Excel workbook");
      return result.artifactId;
    } catch (error) {
      setIsWorkbookExportOptimisticPending(false);
      notifyError(error, "Failed to queue Excel workbook");
      console.error(error);
      return null;
    } finally {
      setIsQueueingWorkbookExport(false);
    }
  };

  const handleSelectTag = async (tagId: string) => {
    try {
      await setIncluded({ tagId: tagId as Id<"takeoffPilotExtractedTags">, included: true });
    } catch (error) {
      notifyError(error, "Failed to add tag");
      console.error(error);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    try {
      await setIncluded({ tagId: tagId as Id<"takeoffPilotExtractedTags">, included: false });
    } catch (error) {
      notifyError(error, "Failed to remove tag");
      console.error(error);
    }
  };

  const handleCreateCustomLineItem = async (args: {
    rawValue: string;
    unitCost: number | null;
  }) => {
    if (!jobId) {
      return;
    }

    try {
      return await createCustomTag({
        jobId,
        rawValue: args.rawValue,
        unitCost: args.unitCost,
      });
    } catch (error) {
      notifyError(error, "Failed to create custom line item");
      console.error(error);
      throw error;
    }
  };

  const handleSearchDocumentTags = async (query: string): Promise<DocumentTagSearchResponse> => {
    if (!jobId) {
      return { status: "no_match", matches: [] };
    }

    return await convex.query(api.takeoffPilot.tags.searchDocumentTagsForCustom, {
      jobId,
      query,
    });
  };

  const handleCreateCustomLineItemsFromDocumentTags = async (
    documentTagIds: Id<"documentIntelligenceCanonicalTags">[]
  ) => {
    if (!jobId) {
      return;
    }

    try {
      const createdTagIds = await createCustomTagsFromDocumentTags({
        jobId,
        documentTagIds,
      });
      if (createdTagIds.length === 0) {
        toast.success("No new custom line items added");
        return;
      }
      toast.success(
        createdTagIds.length === 1
          ? "Added 1 custom line item"
          : `Added ${createdTagIds.length} custom line items`
      );
    } catch (error) {
      notifyError(error, "Failed to add selected tags");
      console.error(error);
      throw error;
    }
  };

  const handleCreateCustomScheduleColumn = useCallback(
    async (header: string) => {
      if (!jobId) {
        return;
      }

      const trimmedHeader = header.trim();
      if (!trimmedHeader) {
        toast.error("Column name is required");
        throw new Error("Column name is required");
      }

      const normalizedHeader = trimmedHeader.toLocaleLowerCase();
      const hasDuplicateHeader = availableScheduleColumns.some(
        (column) => column.value.trim().toLocaleLowerCase() === normalizedHeader
      );
      if (hasDuplicateHeader) {
        toast.error("Column already exists");
        throw new Error("Column already exists");
      }

      const includedTagIds = tags.filter((tag) => tag.included).map((tag) => tag._id);
      if (includedTagIds.length === 0) {
        toast.error("Select at least one line item before adding a column");
        throw new Error("No included line items available for custom column");
      }

      try {
        await addScheduleOverrideColumn({
          jobId,
          tagIds: includedTagIds,
          header: trimmedHeader,
        });
        setOptimisticScheduleHeaders((previous) =>
          previous.includes(trimmedHeader) ? previous : [...previous, trimmedHeader]
        );
        handleSelectScheduleHeader(trimmedHeader);
        toast.success(`Added ${trimmedHeader} column`);
      } catch (error) {
        notifyError(error, "Failed to add column");
        console.error(error);
        throw error;
      }
    },
    [addScheduleOverrideColumn, availableScheduleColumns, handleSelectScheduleHeader, jobId, tags]
  );

  const handleOpenScheduleRow = useCallback((args: {
    tagRawValue: string;
    matchedRow: LinkedSchedulePreviewSummary["matchedRows"][number];
  }) => {
    setSelectedScheduleRow(args);
  }, []);
  const handleNavigableTagIdsChange = useCallback((tagIds: string[]) => {
    setNavigableTagIds((current) => {
      if (current.size === tagIds.length && tagIds.every((tagId) => current.has(tagId))) {
        return current;
      }
      return new Set(tagIds);
    });
  }, []);
  const handleCloseWorkspace = useViewerExit({
    fallback: { to: "/takeoffPilot", title: "TakeoffPilot" },
    returnTo: returnToRevPilotComparisonId
      ? { to: "/revPilot/$comparisonId", params: { comparisonId: returnToRevPilotComparisonId } }
      : null,
  });

  const resolvedJobId = jobId as Id<"takeoffPilotJobs">;
  useEffect(() => {
    if (!isPdfExportOptimisticPending) {
      return;
    }

    if (exportStatus === GLOBAL_PROCESSING_PENDING || exportStatus === GLOBAL_PROCESSING_PROCESSING) {
      setIsPdfExportOptimisticPending(false);
      return;
    }

    if (
      (exportStatus === GLOBAL_PROCESSING_COMPLETED ||
        exportStatus === GLOBAL_PROCESSING_FAILED) &&
      exportStatus !== pdfExportOptimisticStartStatusRef.current
    ) {
      setIsPdfExportOptimisticPending(false);
    }
  }, [isPdfExportOptimisticPending, exportStatus]);

  useEffect(() => {
    if (!isWorkbookExportOptimisticPending) {
      return;
    }

    if (
      workbookExportStatus === GLOBAL_PROCESSING_PENDING ||
      workbookExportStatus === GLOBAL_PROCESSING_PROCESSING
    ) {
      setIsWorkbookExportOptimisticPending(false);
      return;
    }

    if (
      (workbookExportStatus === GLOBAL_PROCESSING_COMPLETED ||
        workbookExportStatus === GLOBAL_PROCESSING_FAILED) &&
      workbookExportStatus !== workbookExportOptimisticStartStatusRef.current
    ) {
      setIsWorkbookExportOptimisticPending(false);
    }
  }, [isWorkbookExportOptimisticPending, workbookExportStatus]);

  const previousPdfExportStatusRef = useRef<ProcessingStateWithIdle | null>(null);
  const previousWorkbookExportStatusRef = useRef<ProcessingStateWithIdle | null>(null);

  useEffect(() => {
    if (!status.isResults) {
      previousPdfExportStatusRef.current = exportStatus;
      return;
    }

    const previousStatus = previousPdfExportStatusRef.current;
    previousPdfExportStatusRef.current = exportStatus;
    if (previousStatus === null || previousStatus === exportStatus) {
      return;
    }

    // Ignore idle→completed on first load; only toast real in-flight finishes.
    const wasInProgress =
      previousStatus === GLOBAL_PROCESSING_PENDING ||
      previousStatus === GLOBAL_PROCESSING_PROCESSING;
    if (!wasInProgress) {
      return;
    }

    if (exportStatus === GLOBAL_PROCESSING_COMPLETED) {
      const currentArtifactId = pdfExport?.artifactId ? String(pdfExport.artifactId) : null;
      if (autoDownloadPdfArtifactId && currentArtifactId === autoDownloadPdfArtifactId) {
        return;
      }
      toast.success("PDF export ready to download");
      return;
    }

    if (exportStatus === GLOBAL_PROCESSING_FAILED) {
      const currentArtifactId = pdfExport?.artifactId ? String(pdfExport.artifactId) : null;
      if (autoDownloadPdfArtifactId && currentArtifactId === autoDownloadPdfArtifactId) {
        return;
      }
      toast.error(pdfExport?.error || "PDF export failed");
    }
  }, [autoDownloadPdfArtifactId, pdfExport?.artifactId, pdfExport?.error, exportStatus, status.isResults]);

  useEffect(() => {
    if (!status.isResults) {
      previousWorkbookExportStatusRef.current = workbookExportStatus;
      return;
    }

    const previousStatus = previousWorkbookExportStatusRef.current;
    previousWorkbookExportStatusRef.current = workbookExportStatus;
    if (previousStatus === null || previousStatus === workbookExportStatus) {
      return;
    }

    const wasInProgress =
      previousStatus === GLOBAL_PROCESSING_PENDING ||
      previousStatus === GLOBAL_PROCESSING_PROCESSING;
    if (!wasInProgress) {
      return;
    }

    if (workbookExportStatus === GLOBAL_PROCESSING_COMPLETED) {
      toast.success("Excel workbook ready to download");
      return;
    }

    if (workbookExportStatus === GLOBAL_PROCESSING_FAILED) {
      toast.error(workbookExport?.error || "Excel workbook export failed");
    }
  }, [workbookExport?.error, workbookExportStatus, status.isResults]);

  const handleExport = async (fileName: string) => {
    setIsSubmittingExport(true);
    try {
      if (exportFormat === "csv") {
        if (!(await handleDownloadCsv(fileName))) return;
        await exportFilenames.saveBrowserExport(
          "takeoff_csv",
          {
            includeSchedule: hasSchedule && includeScheduleInPdf,
            includeTags: includeTagsInPdf,
            includeMeasurements: includeMeasurementsInPdf,
            includeColumns: pdfExportIncludeColumns,
            reportMode: pdfReportMode,
            includeCosts: includeCostsInExport,
          },
          fileName
        );
      } else {
        const artifactId =
          exportFormat === "pdf" ? await handleQueuePdfExport(fileName) : await handleQueueWorkbookExport();
        if (!artifactId) return;
        await exportFilenames.saveForArtifact(artifactId, fileName);
      }
      setIsExportOpen(false);
    } finally {
      setIsSubmittingExport(false);
    }
  };

  if (pageState) {
    return (
      <TakeoffViewerStateShell
        title={viewerTitle}
        subtitle={viewerSubtitle}
        readOnly={!canEdit}
        onBack={handleCloseWorkspace}
      >
        {pageState}
      </TakeoffViewerStateShell>
    );
  }

  // Main full-bleed workspace
  return (
    <div className="@container relative flex h-full flex-col overflow-hidden bg-background">
      {/* Main content - PDF viewer with overlay panel */}
      {status.showWorkspace ? (
        <div className="flex min-h-0 flex-1 flex-col @min-[60rem]:flex-row">
        <div ref={setDrawingWorkspace} className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
          {/* PDF Viewer - full size always */}
          <div className="absolute inset-0">
            {renderTakeoffResultsViewer({
              jobHistory,
              activeSource,
              jobId: resolvedJobId,
              viewerRef: takeoffResultsViewerRef,
              pendingMeasurementFocusRef,
              viewerTags: computed.viewerTags,
              onNavigableTagIdsChange: handleNavigableTagIdsChange,
              viewerSources: resolvedViewerSources,
              preparingSources: allViewerSources,
              onSelectPlanDocumentId: setSelectedPlanDocumentId,
              jobName: viewerTitle,
              jobSubtitle: viewerSubtitle,
              aiReviewContainer,
              aiReviewOpen,
              aiReviewLocation,
              onNavigateAiReview: handleNavigateAiReview,
              onToggleAiReview: () => setAiReviewOpen(open => !open),
              readOnly: !canEdit,
              onClose: handleCloseWorkspace,
              onExport: () => {
                // Reviewers can't generate PDFs or workbooks, so start them on CSV.
                if (!canEdit) setExportFormat("csv");
                setIsExportOpen(true);
              },
              isExportInProgress: isPdfExportInProgress || isWorkbookExportInProgress,
              onCreateCustomLineItem: handleCreateCustomLineItem,
              bottomPanelHeight: isPanelOpen ? panelHeight : 0,
            })}
          </div>

          {/* Table Panel - overlay on top */}
          {isPanelOpen && (
            <ResizableBottomPanel
              isOpen={isPanelOpen}
              height={panelHeight}
              minHeight={150}
              maxHeight={maxResultsHeight}
              onHeightChange={setPanelHeight}
              header={
                <PanelHeader
                  onCreateCustomLineItem={async (args) => {
                    await handleCreateCustomLineItem(args);
                  }}
                  onSearchDocumentTags={handleSearchDocumentTags}
                  onCreateCustomLineItemsFromDocumentTags={handleCreateCustomLineItemsFromDocumentTags}
                  canManageCustomLineItems={status.isResults && !status.isCounting}
                  onCreateCustomScheduleColumn={handleCreateCustomScheduleColumn}
                  canManageCustomScheduleColumns={status.isResults && !status.isCounting}
                  tags={tags}
                  tables={tables}
                  selectedTagIds={tags.filter((t) => t.included).map((t) => t._id)}
                  onSelectTag={handleSelectTag}
                  onRemoveTag={handleRemoveTag}
                  isCounting={status.isCounting}
                  onDropdownSelect={handleDropdownSelect}
                  onDropdownRemove={handleDropdownRemove}
                  onDropdownOpenChange={handleDropdownOpenChange}
                  pendingSelections={isDropdownOpen ? pendingSelections : undefined}
                  hasSchedule={hasSchedule}
                  availableScheduleColumns={availableScheduleColumns}
                  selectedScheduleHeaders={selectedScheduleHeaders}
                  onSelectScheduleHeader={handleSelectScheduleHeader}
                  onRemoveScheduleHeader={handleRemoveScheduleHeader}
                  selectedQuantityColumns={visibleQuantityColumns}
                  onSelectQuantityColumn={handleSelectQuantityColumn}
                  onRemoveQuantityColumn={handleRemoveQuantityColumn}
                  costColumnsVisible={costColumnsVisible}
                  onCostColumnsVisibleChange={setCostColumnsVisible}
                  onHideResults={() => {
                    void handleHideResults();
                  }}
                />
              }
            >
              {isCountsPending || isMeasurementTotalsInitialLoad || !countRows ? (
                <SpinnerState className="h-full" />
              ) : (
                <TagResultsTable
                  tags={tags}
                  hasSchedule={hasSchedule}
                  selectedResults={computed.selectedResults}
                  selectedScheduleHeaders={selectedScheduleHeaders}
                  customScheduleHeaders={customScheduleHeaders}
                  visibleQuantityColumns={visibleQuantityColumnSet}
                  costColumnsVisible={costColumnsVisible}
                  linkedPreviewsByTagId={linkedPreviewsByTagId}
                  scheduleOverridesByTagId={scheduleOverridesByTagId}
                  onUpsertScheduleOverride={async (args) => {
                    await upsertScheduleOverride({ jobId: jobId!, ...args });
                  }}
                  onRemoveScheduleOverrideCells={async (args) => {
                    await removeScheduleOverrideCells({ jobId: jobId!, ...args });
                  }}
                  onResetScheduleOverrides={async (args) => {
                    await resetScheduleOverride({ jobId: jobId!, ...args });
                  }}
                  expandedBreakdownTagRowIds={expandedBreakdownTagRowIds}
                  navigableTagIds={navigableTagIds}
                  onFocusPreviousInstance={(tagId) => {
                    takeoffResultsViewerRef.current?.focusPreviousInstanceForTag(tagId);
                  }}
                  onFocusNextInstance={(tagId) => {
                    takeoffResultsViewerRef.current?.focusNextInstanceForTag(tagId);
                  }}
                  onFocusMeasurementBreakdownRow={(args) => {
                    takeoffResultsViewerRef.current?.focusMeasurementBreakdownRow(args);
                  }}
                  onToggleExpandedBreakdownTagRow={handleToggleExpandedBreakdownTagRow}
                  onOpenScheduleRow={handleOpenScheduleRow}
                  isCounting={status.isCounting}
                  setIncluded={setIncluded}
                  removeCustomTag={removeCustomTag}
                  renameCustomTag={renameCustomTag}
                  setUnitCost={setUnitCost}
                  setCostBasis={setCostBasis}
                  setColorHex={setTagColorHex}
                />
              )}
            </ResizableBottomPanel>
          )}
        </div>
        <AiReviewDock open={AI_COUNTS_ENABLED && canEdit && aiReviewOpen && Boolean(activeSource?.planDocumentId)} onClose={() => setAiReviewOpen(false)} onContainerChange={setAiReviewContainer} />
        </div>
      ) : null}

      {/* Floating toggle button when panel is closed */}
      <FloatingPanelToggle
        isOpen={isPanelOpen}
        onClick={() => setIsPanelOpen(true)}
        tagCount={computed.selectedCount}
      />
      <RowDetailsDialog
        open={selectedScheduleRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedScheduleRow(null);
          }
        }}
        title={selectedScheduleRow ? `Schedule Row · ${selectedScheduleRow.tagRawValue}` : ""}
        description={
          selectedScheduleRow
            ? [
                selectedScheduleRow.matchedRow.tableName,
                selectedScheduleRow.matchedRow.pageNumber
                  ? `Page ${selectedScheduleRow.matchedRow.pageNumber}`
                  : null,
                `Match ${selectedScheduleRow.matchedRow.rowIndex + 1}`,
              ]
                .filter(Boolean)
                .join(" • ")
            : undefined
        }
        cells={selectedScheduleRow?.matchedRow.cells ?? {}}
      />
      <ExportDialog
        open={isExportOpen}
        onOpenChange={setIsExportOpen}
        description={details?.job?.name}
        formats={[{ value: "pdf" }, { value: "excel" }, { value: "csv" }]}
        format={exportFormat}
        onFormatChange={setExportFormat}
        extension={exportFormat === "excel" ? "xlsx" : exportFormat}
        defaultFileName={takeoffExportName}
        optionsSummary={`${pdfReportMode === "full" ? "Full" : "Executive Summary"} · ${pdfExportIncludeColumns.length} columns`}
        options={
          <>
            <ExportOptionGroup title="Report Mode">
              <Select value={pdfReportMode} onValueChange={(value) => setPdfReportMode(value as "executive_summary" | "full")}>
                <SelectTrigger aria-label="Report mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full - include measurement detail</SelectItem>
                  <SelectItem value="executive_summary">Executive Summary - summary rows only</SelectItem>
                </SelectContent>
              </Select>
            </ExportOptionGroup>
            {exportFormat === "pdf" ? (
              <ExportOptionGroup title="Plan Content">
                {hasSchedule ? (
                  <ExportOptionCheckbox checked={includeScheduleInPdf} onCheckedChange={setIncludeScheduleInPdf}>
                    Include schedule pages
                  </ExportOptionCheckbox>
                ) : null}
                <ExportOptionCheckbox checked={includeTagsInPdf} onCheckedChange={setIncludeTagsInPdf}>
                  Include tag callouts on plans
                </ExportOptionCheckbox>
                <ExportOptionCheckbox checked={includeMeasurementsInPdf} onCheckedChange={setIncludeMeasurementsInPdf}>
                  Include measurement markup on plans
                </ExportOptionCheckbox>
              </ExportOptionGroup>
            ) : (
              <ExportOptionGroup title="Cost Columns">
                <ExportOptionCheckbox checked={includeCostsInExport} onCheckedChange={setIncludeCostsInExport}>
                  Include pricing basis, unit cost, and total cost
                </ExportOptionCheckbox>
              </ExportOptionGroup>
            )}
            <ExportOptionGroup
              title="Breakdown Columns"
              hint={
                pdfExportIncludeColumns.length === 0
                  ? "No breakdown columns selected. The export will include the cover page and drawing pages only."
                  : `${pdfExportIncludeColumns.length} breakdown column${pdfExportIncludeColumns.length === 1 ? " is" : "s are"} selected.`
              }
            >
              <MultiSelect
                sections={pdfExportColumnSections}
                selected={pdfExportIncludeColumns}
                onSelect={handleSelectPdfExportColumn}
                onRemove={handleRemovePdfExportColumn}
                onSelectAll={handleSelectAllPdfExportColumns}
                onDeselectAll={handleDeselectAllPdfExportColumns}
                placeholder="Include columns..."
                triggerWidth="w-full"
                dropdownWidth="w-[42rem] max-w-[calc(100vw-4rem)]"
                columns="auto-fit"
                optionMinWidth="10rem"
                showSelectedBadges={false}
                wrapOptionLabels
              />
            </ExportOptionGroup>
          </>
        }
        lastFile={
          exportFormat === "pdf"
            ? lastFileFromArtifact({
                artifact: pdfExport,
                canDownload: canDownloadPdf,
                download: handleDownloadPdf,
                saveName: (fileName) =>
                  pdfExport ? exportFilenames.saveForArtifact(pdfExport.artifactId, fileName) : Promise.resolve(),
              })
            : exportFormat === "excel"
              ? lastFileFromArtifact({
                  artifact: workbookExport,
                  canDownload: canDownloadWorkbook,
                  download: handleDownloadExcel,
                  saveName: (fileName) =>
                    workbookExport
                      ? exportFilenames.saveForArtifact(workbookExport.artifactId, fileName)
                      : Promise.resolve(),
                })
              : null
        }
        notice={
          !aiExportReady
            ? aiExport?.reason ?? "Wait for AI review readiness"
            : exportFormat !== "csv" && !canEdit
            ? "Reviewers can download the last file but can't generate a new one."
            : exportFormat === "csv" && !canDownloadCsv
              ? "Select tags with results to export a CSV."
              : null
        }
        onExport={handleExport}
        isExporting={isSubmittingExport}
        exportDisabled={
          exportFormat === "csv"
            ? !canDownloadCsv
            : !canEdit || (exportFormat === "pdf" ? !canExportPdf : !canExportWorkbook)
        }
      />
    </div>
  );
}
